import { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Check, ArrowUpCircle, ArrowDownCircle } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '@/lib/api';
import { billingIntervalLabel, classifyChange } from '@/lib/plans';
import { PublicPlan, formatPlanPrice } from '@/components/PlanSelector';

export interface CurrentPlanInfo {
  key: string;
  name: string;
  price: number | null;
  interval: string;
}

interface ChangeResult {
  effect: 'upgraded' | 'downgrade_scheduled' | 'change_canceled';
  effectiveAt?: string | null;
  chargedAmount?: number; // minor units (e.g. piastres/cents)
  currency?: string;
}

export default function ChangePlanDialog({
  open,
  onOpenChange,
  workspaceId,
  currentPlan,
  pendingPlanKey,
  renewsAt,
  availablePlan,
  onChanged,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  currentPlan: CurrentPlanInfo | null;
  pendingPlanKey: string | null;
  renewsAt: string | null;
  // The workspace's bound custom/enterprise plan (hidden from the public catalog).
  availablePlan?: PublicPlan | null;
  onChanged: (result: ChangeResult) => void;
}) {
  const [plans, setPlans] = useState<PublicPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<PublicPlan | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelected(null);
    setLoading(true);
    apiFetch<{ plans: PublicPlan[] }>('/plans')
      .then((r) => setPlans(r.plans || []))
      .catch(() => { /* noop */ })
      .finally(() => setLoading(false));
  }, [open]);

  // Switchable plans: public catalog + the bound custom plan, deduped, excluding
  // "contact us" (price == null) plans which can't be self-served.
  const options = useMemo(() => {
    const extra = availablePlan ? [availablePlan] : [];
    const extraKeys = new Set(extra.map((p) => p.key));
    return [...extra, ...plans.filter((p) => !extraKeys.has(p.key))].filter((p) => p.price != null);
  }, [plans, availablePlan]);

  const renewDateLabel = renewsAt ? new Date(renewsAt).toLocaleDateString() : 'the end of your billing period';

  const change = classifyChange(currentPlan, selected ?? { key: '', price: null, interval: 'month' });

  const confirm = async () => {
    if (!selected) return;
    try {
      setSubmitting(true);
      const res = await apiFetch<ChangeResult>(`/workspaces/${workspaceId}/billing/change-plan`, {
        method: 'POST',
        body: JSON.stringify({ planKey: selected.key }),
      });
      onChanged(res);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not change plan');
      setSubmitting(false);
    }
  };

  // Confirmation step.
  if (selected) {
    const isUpgrade = change === 'upgrade';
    return (
      <Dialog open={open} onOpenChange={(o) => { if (!submitting) onOpenChange(o); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{isUpgrade ? 'Upgrade' : 'Downgrade'} to {selected.name}?</DialogTitle>
            <DialogDescription>
              {isUpgrade
                ? `You'll switch to ${selected.name} now and be charged a prorated amount for the rest of this billing period. Your new credit allotment becomes available immediately.`
                : `You'll keep ${currentPlan?.name || 'your current plan'} until ${renewDateLabel}, then move to ${selected.name}. No refund is issued and your current credits stay.`}
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-md border p-4 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">New plan</span>
              <span className="font-medium">{selected.name}</span>
            </div>
            <div className="flex items-center justify-between mt-1">
              <span className="text-muted-foreground">Price</span>
              <span className="font-medium">
                {formatPlanPrice(selected)}{selected.price != null && ` / ${billingIntervalLabel(selected.interval)}`}
              </span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelected(null)} disabled={submitting}>Back</Button>
            <Button onClick={confirm} disabled={submitting}>
              {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              {isUpgrade ? 'Upgrade now' : 'Schedule downgrade'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // Plan list step.
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Change plan</DialogTitle>
          <DialogDescription>
            Upgrades take effect immediately; downgrades start at the end of your current billing period.
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 py-2">
            {options.map((plan) => {
              const isCurrent = currentPlan?.key === plan.key;
              const isPending = pendingPlanKey === plan.key;
              const kind = classifyChange(currentPlan, plan);
              return (
                <Card key={plan.key} className={`p-5 flex flex-col ${isCurrent ? 'ring-2 ring-primary' : ''}`}>
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="text-lg font-bold">{plan.name}</h3>
                    {isCurrent && <Badge variant="secondary">Current</Badge>}
                    {isPending && <Badge variant="outline">Scheduled</Badge>}
                  </div>
                  <div className="mt-2 mb-1">
                    <span className="text-2xl font-bold">{formatPlanPrice(plan)}</span>
                    {plan.price != null && <span className="text-muted-foreground"> / {billingIntervalLabel(plan.interval)}</span>}
                  </div>
                  <p className="text-sm text-muted-foreground mb-4">
                    {plan.creditLimit?.toLocaleString()} credits / {billingIntervalLabel(plan.interval)}
                  </p>
                  <ul className="space-y-2 text-sm flex-1 mb-5">
                    {(plan.marketingFeatures || []).map((f) => (
                      <li key={f} className="flex items-start gap-2">
                        <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                        <span className="text-muted-foreground">{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    className="w-full"
                    variant={kind === 'downgrade' ? 'outline' : 'default'}
                    disabled={isCurrent || isPending}
                    onClick={() => setSelected(plan)}
                  >
                    {isCurrent ? 'Current plan' : isPending ? 'Scheduled' : kind === 'downgrade'
                      ? <><ArrowDownCircle className="h-4 w-4 mr-2" />Downgrade</>
                      : <><ArrowUpCircle className="h-4 w-4 mr-2" />Upgrade</>}
                  </Button>
                </Card>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
